import { useState, useEffect } from 'react';
import { DollarSign, Info } from 'lucide-react';
import { OrganizerIncomeModal } from './OrganizerIncomeModal';
import type { PaymentType } from '@/types';

interface PriceBreakdownProps {
  basePrice: number;
  commission: number;
  paymentMethod?: string;
  showPaymentMethod?: boolean;
  showTitle?: boolean;
  showOrganizerIncome?: boolean;
  paymentType?: PaymentType;
  className?: string;
}

export function PriceBreakdown({
  basePrice,
  commission,
  paymentMethod,
  showPaymentMethod = false,
  showTitle = true,
  showOrganizerIncome = false,
  paymentType,
  className = '',
}: PriceBreakdownProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleOpenModal = () => {
      if (showOrganizerIncome && paymentType) {
        setIsModalOpen(true);
      }
    };

    window.addEventListener('openOrganizerIncomeModal', handleOpenModal);
    return () => {
      window.removeEventListener('openOrganizerIncomeModal', handleOpenModal);
    };
  }, [showOrganizerIncome, paymentType]);
  const formatPrice = (value: number) => {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-gray-500" />
            <h4 className="text-sm font-semibold text-gray-900">
              Precio de inscripción por jugador
            </h4>
          </div>
          {showOrganizerIncome && paymentType && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="text-blue-600 transition-colors hover:text-blue-800"
              aria-label="Ver detalle de ingreso del organizador"
            >
              <Info className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Price Breakdown */}
      <div className="space-y-1.5">
        {/* Base Price */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-1.5">
          <span className="text-sm text-gray-700">Precio del torneo:</span>
          <span className="text-sm font-medium text-gray-900">
            ${formatPrice(basePrice)}
          </span>
        </div>

        {/* Commission */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-1.5">
          <span className="text-sm text-gray-700">Comisión GetMatch:</span>
          <span className="text-sm font-medium text-gray-900">
            +${formatPrice(commission)}
          </span>
        </div>

        {/* Final Price */}
        <div className="flex items-center justify-between pt-1.5">
          <span className="text-sm font-semibold text-gray-900">
            Precio final:
          </span>
          <span className="text-sm font-bold text-gray-900">
            ${formatPrice(basePrice + commission)}
          </span>
        </div>
      </div>

      {/* Payment Method */}
      {showPaymentMethod && paymentMethod && (
        <div className="mt-2 border-t border-gray-200 pt-2">
          <div className="flex items-center space-x-2">
            <div className="h-1.5 w-1.5 rounded-full bg-gray-400"></div>
            <span className="text-xs text-gray-600">
              Método de pago: {paymentMethod}
            </span>
          </div>
        </div>
      )}

      {/* Organizer Income Modal */}
      {showOrganizerIncome && paymentType && (
        <OrganizerIncomeModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          basePrice={basePrice}
          commission={commission}
        />
      )}
    </div>
  );
}
